import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { notification } from 'antd';

import { generateRideRequestSteps, LodashHelper, transformRideRequestFields } from '../../../utils';
import { Stepper } from '../../../components';
import { BackIcon } from '../../../assets';
import { renderSteps } from './renderSteps';
import {
  setCurrentStep,
  getDraft,
  createDraft,
  updateDraft,
  resetDraft,
  saveDraft,
  deleteDraft,
  setUnSavedDraft,
} from '../../../store/drafts';
import { getPlatformSettings, getAdditionalRideOptions } from '../../../store/preferences';
import healthOrganizations, { fetchAllHealthOrganizations, fetchHOPreferences } from '../../../store/health_organizations';
import { stepperInfo } from '../../../components/forms/CreateRide/stepperInfo';
import { usePreventLeave } from '../../../hooks/usePreventLeave';
import {
  COVID_ERROR_MESSAGE,
  COVID_OPTIONS,
  MANDATORY_LEVEL,
  RIDE_REQUEST_FIELDS,
  THIRD_STEP_RIDE_REQUEST_FIELDS,
  UBER_INVALID_PHONE_MESSAGE,
  VEHICLE_TYPES,
} from '../../../constants/constants';
import { renderEditLeaveModal } from '../EditDraft/renderEditLeaveModal';
import { renderCreateRideModal } from './renderCreateRideModal';
import { useCreateRideHandlers } from '../../../hooks/useCreateRideHandlers';
import { createRideSchema } from '../../../utils/validations';
import { checkStepIsDisabled, checkStepStatus } from '../../../utils/helpers';
import { normalizeRideUberPhoneNumbers } from '../../../utils/phoneNumberHelper';
import styles from './index.module.css';
import { useSelector } from 'react-redux';
import { Navigation } from '../../../utils/navigation';


const CreateRide = ({
  step,
  draftId,
  getDraft,
  airportBufferTime,
  draft,
  riderDetails,
  history,
  getPlatformSettings,
  platformPreferences,
  defaultTimeZone,
  fetchHOPreferences,
  HOPreferences,
  getAdditionalRideOptions,
  rideOptions,
  isDraftEditMode = false,
  setUnSavedDraft,
  healthOrganizations,
  fetchAllHealthOrganizations,
}) => {
  const user = useSelector((state) => state.authorization.user);

  const [rideRequestFields, setRideRequestFields] = useState(null);
  const [showThirdStep, setShowThirdStep] = useState(true);

  const [showCovidField, setShowCoviedField] = useState(false);

  const [formikValues, setFormikValues] = useState(null);

  const [formIsValid, setFormIsValid] = useState(false);

  const [draftIsValid, setDraftIsValid] = useState(null);
  const COMPACT_NOTIFICATION_STYLE = { width: 'min(480px, 95vw)' };


  const [visibleModal, setModalVisible, unblockHistory, location] = usePreventLeave(
    formikValues,
    stepperInfo[0].values,
  );
  const navigate = Navigation();
  const isUberEligibleFromAuth = useSelector((state) => state.authorization?.isUberEligible);
  const syncFormikValues = useCallback(
    (values, isValid, path) => {
      setFormikValues(values);
      setFormIsValid((prevValues) => {
        return {
          ...prevValues,
          [path]: isValid,
        };
      });
    },

    [],
  );

  const isUberDraft = useMemo(
    () =>
      (draft?.step2?.vehicleType ??
        formikValues?.vehicleType ??
        formikValues?.step2?.vehicleType) === VEHICLE_TYPES.UBER_AMBULATORY_RIDE,
    [draft?.step2?.vehicleType, formikValues?.vehicleType, formikValues?.step2?.vehicleType],
  );

  const costCenterField = useMemo(
    () => ({
      show: rideRequestFields?.some((field) => field.name === RIDE_REQUEST_FIELDS.costCenter),
      isRequired:
        rideRequestFields?.find((field) => field.name === RIDE_REQUEST_FIELDS.costCenter)?.value ===
        MANDATORY_LEVEL.MANDATORY,
    }),
    [rideRequestFields],
  );
  const showAdditionalSourceField = useMemo(
    () => ({
      show: rideRequestFields?.some((field) => field.name === RIDE_REQUEST_FIELDS.additionalFundingSource),
      isRequired:
        rideRequestFields?.find((field) => field.name === RIDE_REQUEST_FIELDS.additionalFundingSource)?.value ===
        MANDATORY_LEVEL.MANDATORY,
    }),
    [rideRequestFields],
  );
  const showDocumentReasonForRideField = useMemo(
    () => ({
      show: rideRequestFields?.some((field) => field.name === RIDE_REQUEST_FIELDS.documentReasonForRide),
      isRequired:
        rideRequestFields?.find((field) => field.name === RIDE_REQUEST_FIELDS.documentReasonForRide)?.value ===
        MANDATORY_LEVEL.MANDATORY,
    }),
    [rideRequestFields],
  );

  useEffect(() => {
    rideRequestFields &&
      setShowCoviedField(rideRequestFields?.some((field) => field.name === RIDE_REQUEST_FIELDS.covid19Relation));

    user.permissions?.orgList?.length > 1 && fetchAllHealthOrganizations();
    return;
  }, [rideRequestFields, fetchAllHealthOrganizations, user.permissions]);

  useEffect(() => {
    draft &&
      costCenterField &&
      showAdditionalSourceField &&
      showDocumentReasonForRideField &&
      createRideSchema({
        optionalStepRules: { costCenterField, showAdditionalSourceField, showDocumentReasonForRideField },
        covidFieldIsRequired: showCovidField,
        validateOrg: user?.permissions?.orgList || false,
      })
        .validate(draft, { abortEarly: false })
        .then((res) => {
          return setDraftIsValid([true, true, true, true, true]);
        })
        .catch((error) => {
          const step1IsValid = !error?.inner?.some((innerError) => innerError.path.includes('step1'));
          const step2IsValid = !error?.inner?.some((innerError) => innerError.path.includes('step2'));
          const step3IsValid = !error?.inner?.some((innerError) => innerError.path.includes('step3'));
          const step4IsValid =
            step3IsValid &&
            !error?.inner?.some((innerError) => innerError.path.includes('step4'));
          setDraftIsValid([step1IsValid, step2IsValid, step3IsValid, step4IsValid]);
        });

    return;
  }, [draft, costCenterField, showAdditionalSourceField, showDocumentReasonForRideField, showCovidField]);

  const {
    handleDelete,
    handleStay,
    handlePreviousStep,
    handleSaveAndClose,
    handleSave,
    handleBack,
    handleLeave,
    handleSubmit,
  } = useCreateRideHandlers({
    Navigation: navigate,
    step,
    history,
    formikValues,
    draft,
    draftId,
    showThirdStep,
    unblockHistory,
    setModalVisible,
    location,
    draftIsValid,
    isUberEligibleFromAuth,
  });

  useEffect(() => {
    if (draftId) getDraft(draftId);
    return;
  }, [draftId, getDraft]);

  useEffect(() => {
    if (rideRequestFields)
      setShowThirdStep(rideRequestFields.some((field) => THIRD_STEP_RIDE_REQUEST_FIELDS[field.name]));

    return;
  }, [rideRequestFields]);

  useEffect(() => {
    if (HOPreferences) setRideRequestFields(transformRideRequestFields(HOPreferences?.rideRequestFields));
    return;
  }, [HOPreferences]);

  useEffect(() => {
    fetchHOPreferences();
    return;
  }, [fetchHOPreferences]);

  useEffect(() => {
    getAdditionalRideOptions();
    return;
  }, [getAdditionalRideOptions]);

  useEffect(() => {
    getPlatformSettings();
    return;
  }, [getPlatformSettings]);

  useEffect(() => () => { resetDraft(); return; });

  useEffect(() => {
    if (step !== 4) {
      setUnSavedDraft({ [`step${step + 1}`]: LodashHelper.trimValues(formikValues) });
    }
    return;
  }, [draft, draftId, setUnSavedDraft, step, formikValues]);

  const handleNavigateThroughStepper = async (current) => {
    const vehicleTypeOnStep2 =
      formikValues?.vehicleType ??
      formikValues?.step2?.vehicleType ??
      draft?.step2?.vehicleType;
    const isUberRide = vehicleTypeOnStep2 === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;
    const movingForward = current > step;
    const jumpingToStepThreeOrLater = current >= 2;
    const currentCovidSelection =
      formikValues?.covid19Relation ??
      formikValues?.step1?.covid19Relation ??
      draft?.step1?.covid19Relation;
    const hasConfirmedCovidCase = currentCovidSelection === COVID_OPTIONS[1].value;

    const covidRequiredButMissing =
      showCovidField &&
      (currentCovidSelection == null || String(currentCovidSelection).trim() === '');

    if (movingForward && step === 0 && current >= 1 && covidRequiredButMissing) {
      notification.error({
        className: 'create-ride-handler-toast',
        message: COVID_ERROR_MESSAGE,
        duration: 6,
        placement: 'top',
        style: COMPACT_NOTIFICATION_STYLE,
      });
    
      return;
    }

    if (movingForward && current > 0 && hasConfirmedCovidCase) {
      notification.error({
        className: 'create-ride-handler-toast',
        message: COVID_ERROR_MESSAGE,
        duration: 6,
        placement: 'top',
        style: COMPACT_NOTIFICATION_STYLE,
      });
      
      return;
    }

    if (isUberRide && movingForward && jumpingToStepThreeOrLater) {
      const stepOneValues = formikValues?.step1 ?? draft?.step1 ?? null;
      const normalizedStepOne = normalizeRideUberPhoneNumbers(stepOneValues);

      if (!normalizedStepOne.isValid) {
        notification.error({
          className: 'create-ride-handler-toast',
          message: UBER_INVALID_PHONE_MESSAGE,
          duration: 6,
          placement: 'top',
          style: COMPACT_NOTIFICATION_STYLE,

        });
        return;
      }
    }

    const checkIfFormIsValid = Number(step) !== 4 ? formIsValid[`step${step + 1}`] : true;
    const allowNavigateForward =
      (draftIsValid?.[current] && checkIfFormIsValid && current > step) ||
      (formIsValid[`step${step + 1}`] && current - step === 1) ||
      draftIsValid?.[current - 1] ||
      (current === 4 && !showThirdStep && !isUberRide);
    const allowNavigateBack = draftIsValid?.[current] && (checkIfFormIsValid || current < step);

    if (allowNavigateForward) {
      handleSubmit(formikValues, String(current));
    } else if (allowNavigateBack) {
      handlePreviousStep(String(current));
    }
  };

  const steps = useMemo(() => {
    const generatedSteps = generateRideRequestSteps().map((s, index, steps) => {
      const stepIsDisabled = checkStepIsDisabled({
        steps,
        step,
        index,
        draftIsValid,
        formIsValid,
        showThirdStep,
        forceShowAdditionalStep: isUberDraft,
      });

      const stepStatus = checkStepStatus({
        steps,
        step,
        index,
        draftIsValid,
        formIsValid,
        showThirdStep,
        forceShowAdditionalStep: isUberDraft,
      });

      s.disabled = stepIsDisabled;
      s.status = stepStatus;

      return s;
    });

    return generatedSteps;
  }, [draftIsValid, formIsValid, showThirdStep, step, isUberDraft]);

  // Add local state to control detail view from QuickSchedule
  // const [showDetailView, setShowDetailView] = useState(false);

  // // Handler to show detail view from QuickSchedule
  // const handleShowDetailView = () => setShowDetailView(true);

  return (
    <div className={styles.createRide}>
      <div className={styles.header}>
        <div className={styles.backBtn} onClick={handleBack}>
          <BackIcon width='26px' height='26px' fill='#E1F4D3' />
        </div>
        <h1 className={styles.pageTitle}> Request a Ride</h1>
      </div>
      <div className={styles.wizard}>

        <div className={styles.steps}>
          <Stepper
            currentStep={step}
            showThirdStep={showThirdStep}
            steps={steps}
            handleNavigateThroughStepper={handleNavigateThroughStepper}
          />
        </div>

        {/* Main conditional rendering logic */}



        {renderSteps({
          step,
          draft,
          airportBufferTime,
          handleSaveAndClose,
          handleSubmit,
          handlePreviousStep,
          platformPreferences,
          rideOptions,
          defaultTimeZone,
          handleSave,
          rideRequestFields,
          syncFormikValues,
          showCovidField,
          costCenterField,
          showAdditionalSourceField,
          showDocumentReasonForRideField,
          user,
          healthOrganizations,
          maxAdditionalPassengers: HOPreferences?.maxAdditionalPassengers,
        })
        }

        {/* <QuickSchedule
  step={step}
  draft={draft}
  airportBufferTime={airportBufferTime}
  platformPreferences={platformPreferences}
  defaultTimeZone={defaultTimeZone}
  handleSave={handleSave}
  riderDetails={riderDetails}
  rideRequestFields={rideRequestFields}
  syncFormikValues={syncFormikValues}
  showCovidField={showCovidField}
  handleQuickSubmit={handleQuickSubmit}
  costCenterField={costCenterField}
  showAdditionalSourceField={showAdditionalSourceField}
  showDocumentReasonForRideField={showDocumentReasonForRideField}
  onShowDetailView={handleShowDetailView}
/> */}


      </div>

      {isDraftEditMode
        ? renderEditLeaveModal({ visibleModal, handleStay, handleLeave })
        : renderCreateRideModal({
          draftId,
          visibleModal,
          handleStay,
          handleDelete,
          handleSaveAndClose,
          handleLeave,
        })}
    </div>
  );
};

const mapStateToProps = (state) => ({
  step: state.drafts.currentStep,
  draftId: state.drafts.draftId,
  draft: state.drafts.draft,
  riderDetails: state.riders.riderDetails,
  platformPreferences: state.preferences.platformPreferences,
  defaultTimeZone: state.authorization?.user?.preferredTimezone,
  HOPreferences: state.healthOrganizations.HOPreferences,
  rideOptions: state.preferences.rideOptions?.filter((option) => !option.disabled),
  userInactivityModalVisible: state.common.userInactivityModalVisible,
  airportBufferTime: state?.healthOrganizations?.HOPreferences?.airportBufferTime,
  healthOrganizations: state.healthOrganizations.healthOrganizations,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setCurrentStep,
      getDraft,
      createDraft,
      updateDraft,
      resetDraft,
      saveDraft,
      deleteDraft,
      getPlatformSettings,
      fetchHOPreferences,
      getAdditionalRideOptions,
      setUnSavedDraft,
      fetchAllHealthOrganizations,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(CreateRide);
